import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import s from './LegalPage.module.scss';

const LAST_UPDATED = 'June 2, 2026';

function LegalLayout({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <div className={s.page}>
      <article className={s.doc}>
        <header className={s.head}>
          <p className={s.brand}>ultimumIT portal</p>
          <h1 className={s.title}>{title}</h1>
          <p className={s.updated}>Last updated: {LAST_UPDATED}</p>
        </header>
        {children}
        <footer className={s.footer}>
          <Link to="/legal/eula">Terms of Use</Link>
          <span aria-hidden="true">·</span>
          <Link to="/legal/privacy">Privacy Policy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/">Return to portal</Link>
        </footer>
      </article>
    </div>
  );
}

/**
 * Public Terms of Use / EULA. Baseline copy describing how the portal may be
 * used — pending ultimumIT's review of jurisdiction + entity specifics before
 * the Intuit production submission (T-080).
 */
export function EulaPage(): ReactNode {
  return (
    <LegalLayout title="Terms of Use">
      <p className={s.lede}>
        These Terms of Use (the “Terms”) govern access to and use of the ultimumIT onboarding and
        billing portal at portal.ultimumit.com (the “Portal”), operated by ultimumIT (“ultimumIT”,
        “we”, “us”). By accessing the Portal — whether as an authorized administrator or as a
        contractor or vendor invited through a secure link — you agree to these Terms.
      </p>

      <h2 className={s.h2}>1. Who may use the Portal</h2>
      <p>
        The Portal is a private business tool, not a public service. Access is limited to (a)
        ultimumIT administrators and (b) contractors and vendors whom ultimumIT invites to complete
        onboarding, execute agreements, and maintain their engagement records. Each invitation link
        is personal to the recipient and must not be shared.
      </p>

      <h2 className={s.h2}>2. License</h2>
      <p>
        Subject to these Terms, ultimumIT grants you a limited, non-exclusive, non-transferable,
        revocable license to use the Portal solely for its intended purpose: contractor onboarding,
        execution of subcontractor agreements, and the related billing and accounting workflow.
      </p>

      <h2 className={s.h2}>3. Acceptable use</h2>
      <p>You agree that you will not:</p>
      <ul className={s.list}>
        <li>access or attempt to access data or accounts that are not yours;</li>
        <li>provide information you know to be false, including identity, tax, or banking details;</li>
        <li>interfere with, probe, or attempt to circumvent the Portal’s security; or</li>
        <li>use automated means to scrape, crawl, or extract data from the Portal.</li>
      </ul>

      <h2 className={s.h2}>4. Account and link security</h2>
      <p>
        You are responsible for keeping your credentials and invitation links confidential and for
        all activity under your access. Notify us promptly of any suspected unauthorized use.
      </p>

      <h2 className={s.h2}>5. Electronic signatures</h2>
      <p>
        Agreements presented through the Portal are signed electronically through our e-signature
        provider. You agree that your electronic signature is the legal equivalent of your manual
        signature and that the executed agreement is binding.
      </p>

      <h2 className={s.h2}>6. Intellectual property</h2>
      <p>
        The Portal, its software, and its design are owned by ultimumIT and its licensors. These
        Terms grant you no ownership rights. Documents and information you submit remain yours; you
        grant ultimumIT the right to use them to provide the service described here.
      </p>

      <h2 className={s.h2}>7. Disclaimer</h2>
      <p>
        The Portal is provided “as is” and “as available,” without warranties of any kind, to the
        fullest extent permitted by law.
      </p>

      <h2 className={s.h2}>8. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, ultimumIT will not be liable for indirect,
        incidental, special, consequential, or punitive damages arising from your use of the
        Portal.
      </p>

      <h2 className={s.h2}>9. Termination</h2>
      <p>
        We may suspend or revoke access at any time, including when an engagement ends or these
        Terms are violated. Provisions that by their nature should survive termination will survive.
      </p>

      <h2 className={s.h2}>10. Governing law</h2>
      <p>
        These Terms are governed by the laws of the jurisdiction in which ultimumIT is established,
        without regard to its conflict-of-laws rules.
      </p>

      <h2 className={s.h2}>11. Contact</h2>
      <p>
        Questions about these Terms may be sent to <a href="mailto:support@ultimumit.com">support@ultimumit.com</a>.
      </p>
    </LegalLayout>
  );
}

/**
 * Public Privacy Policy. Baseline copy grounded in the data the Portal actually
 * handles (contractor PII, banking/ACH, EIN, uploaded documents, e-signatures,
 * QuickBooks tokens) and how it is secured. Pending ultimumIT review before the
 * Intuit production submission (T-080).
 */
export function PrivacyPage(): ReactNode {
  return (
    <LegalLayout title="Privacy Policy">
      <p className={s.lede}>
        This Privacy Policy explains what information the ultimumIT onboarding and billing portal at
        portal.ultimumit.com (the “Portal”) collects, how we use and protect it, and with whom we
        share it. It applies to ultimumIT administrators and to the contractors and vendors we
        onboard.
      </p>

      <h2 className={s.h2}>Information we collect</h2>
      <p>From administrators:</p>
      <ul className={s.list}>
        <li>name, work email address, and an account password (stored only as a secure hash).</li>
      </ul>
      <p>From contractors and vendors, as part of onboarding:</p>
      <ul className={s.list}>
        <li>legal and business name, contact details, and mailing address;</li>
        <li>taxpayer identification number (EIN);</li>
        <li>bank account and routing number used to set up payment (ACH);</li>
        <li>
          uploaded documents you provide (such as identification, work-authorization, and
          qualification documents);
        </li>
        <li>your electronic signature and the executed subcontractor agreement; and</li>
        <li>engagement details such as client, role, and rate.</li>
      </ul>

      <h2 className={s.h2}>How we use it</h2>
      <p>We use this information only to operate the engagement, specifically to:</p>
      <ul className={s.list}>
        <li>complete onboarding and prepare and execute subcontractor agreements;</li>
        <li>set up and process payment;</li>
        <li>generate monthly billing;</li>
        <li>create the corresponding vendor and service records in our accounting system; and</li>
        <li>send you transactional email related to your onboarding and engagement.</li>
      </ul>

      <h2 className={s.h2}>How we protect it</h2>
      <ul className={s.list}>
        <li>All traffic to the Portal is encrypted in transit (HTTPS/TLS).</li>
        <li>
          Sensitive financial identifiers — your bank/ACH details and EIN — and our accounting
          integration tokens are encrypted at rest using AES-256-GCM.
        </li>
        <li>
          Uploaded documents are held in private storage and are reachable only through short-lived,
          access-controlled links.
        </li>
        <li>Administrator passwords are stored only as salted hashes, never in plain text.</li>
        <li>Access is role-restricted and significant actions are recorded in an audit log.</li>
      </ul>

      <h2 className={s.h2}>How we share it</h2>
      <p>
        We do not sell your information. We share it only with the service providers needed to run
        the Portal, and only to the extent required to provide the service:
      </p>
      <ul className={s.list}>
        <li>Intuit QuickBooks — to record vendor, service, and billing data in our accounting;</li>
        <li>our e-signature provider — to present and execute agreements;</li>
        <li>our email provider — to deliver transactional messages; and</li>
        <li>our cloud hosting provider — for application hosting, database, and file storage.</li>
      </ul>
      <p>We may also disclose information when required by law.</p>

      <h2 className={s.h2}>How long we keep it</h2>
      <p>
        We retain your information for the duration of your engagement with ultimumIT and for as
        long as needed afterward to meet legal, tax, and audit obligations.
      </p>

      <h2 className={s.h2}>Your choices</h2>
      <p>
        To access, correct, or ask a question about your information, contact us at{' '}
        <a href="mailto:privacy@ultimumit.com">privacy@ultimumit.com</a>.
      </p>

      <h2 className={s.h2}>Changes</h2>
      <p>
        We may update this policy from time to time; the “Last updated” date above reflects the most
        recent change.
      </p>
    </LegalLayout>
  );
}
